import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useQuery } from "react-query";
import { ThreeGlobe } from "../ThreeGlobe/ThreeGlobe";
import { SatelliteTleData } from "./types";
import { fetchTleData } from "./axios";
import { useAppSettings } from "../../context/AppSettingsContext";
import { SatelliteListPanel } from "../SatelliteListPanel/SatelliteListPanel";

import { usePassSimulation } from "../PassPlanner/PassSimulationContext";
import { usePassPlanner } from "../PassPlanner/PassPlannerContext";
import { PassLocationPrompt } from "../PassPlanner/PassLocationPrompt";

const PassSimulationControls = lazy(() =>
  import("../PassPlanner/PassSimulationControls").then((module) => ({
    default: module.PassSimulationControls,
  })),
);
const PassPlanner = lazy(() => import("../PassPlanner/PassPlanner"));
const MapView = lazy(() => import("../MapView/MapView"));

export const SatDataLoader = () => {
  const { open: plannerOpen, location, picking } = usePassPlanner();
  const { session, returnLive } = usePassSimulation();
  const {
    data: tleData,
    error: tleError,
    isLoading: isLoadingTleData,
    isFetching,
    refetch,
  } = useQuery<SatelliteTleData[], Error>("tleData", fetchTleData, {
    refetchInterval: 5700000,
  });
  const { showWaffleIcon, renderAlternateMap, showSatTrails } =
    useAppSettings();
  const [plannerLoaded, setPlannerLoaded] = useState(false);
  useEffect(() => {
    if (plannerOpen) setPlannerLoaded(true);
  }, [plannerOpen]);
  const [focusTargetSatellite, setFocusTargetSatellite] =
    useState<SatelliteTleData | null>(null);
  const [focusAnchorEl, setFocusAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedSatelliteId, setSelectedSatelliteId] = useState<number | null>(
    null,
  );

  const displayedSatellites = useMemo(() => {
    if (session) return [session.satellite];
    return tleData ?? [];
  }, [session, tleData]);

  useEffect(() => {
    setFocusTargetSatellite(null);
    setFocusAnchorEl(null);
  }, [session]);

  useEffect(() => {
    if (picking) returnLive();
  }, [picking, returnLive]);

  const handleFocusSatellite = (
    satellite: SatelliteTleData,
    anchorEl: HTMLElement,
  ) => {
    setFocusTargetSatellite(satellite);
    setFocusAnchorEl(anchorEl);
    setSelectedSatelliteId(satellite.info.satid);
  };

  const handleFocusComplete = useCallback(
    () => setFocusTargetSatellite(null),
    [],
  );

  if (isLoadingTleData) {
    return (
      <Box
        role="status"
        sx={{
          m: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Loading constellation…
        </Typography>
      </Box>
    );
  }

  if (tleError && !tleData) {
    return (
      <Box role="alert" sx={{ m: "auto", p: 3, textAlign: "center" }}>
        <Typography sx={{ fontWeight: 600 }}>
          Satellite data is unavailable
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, mb: 2 }}
        >
          Check your connection and try again.
        </Typography>
        <Button variant="outlined" onClick={() => refetch()}>
          Try again
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        width: "100%",
        minWidth: 0,
        minHeight: 0,
        flex: 1,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {!renderAlternateMap && (
          <ThreeGlobe
            tleData={displayedSatellites}
            showSatTrails={showSatTrails || Boolean(session)}
            focusTargetSatellite={focusTargetSatellite}
            focusAnchorEl={focusAnchorEl}
            onFocusComplete={handleFocusComplete}
          />
        )}
        {renderAlternateMap && (
          <Suspense
            fallback={
              <Box
                role="status"
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Loading map…
                </Typography>
              </Box>
            }
          >
            <MapView
              tleData={displayedSatellites}
              showWaffleIcon={showWaffleIcon}
              focusTargetSatellite={focusTargetSatellite}
              focusAnchorEl={focusAnchorEl}
              onFocusComplete={handleFocusComplete}
            />
          </Suspense>
        )}
        {tleError && tleData && !session && (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                disabled={isFetching}
                onClick={() => refetch()}
                sx={{ minHeight: 44 }}
              >
                Retry
              </Button>
            }
            sx={{
              position: "absolute",
              top: picking ? 86 : 16,
              left: 16,
              right: 16,
              width: "fit-content",
              maxWidth: "calc(100% - 32px)",
              mx: "auto",
              alignItems: "center",
              zIndex: 2,
            }}
          >
            Couldn’t refresh satellite data. Showing the last loaded data.
          </Alert>
        )}
        {!session && <PassLocationPrompt />}
        {session && (
          <Suspense fallback={null}>
            <PassSimulationControls />
          </Suspense>
        )}
      </Box>
      {(plannerOpen || location || plannerLoaded) && (
        <Suspense fallback={null}>
          <PassPlanner satellites={tleData ?? []} />
        </Suspense>
      )}
      {!session && (
        <SatelliteListPanel
          satellites={tleData ?? []}
          selectedSatelliteId={selectedSatelliteId}
          onFocusSatellite={handleFocusSatellite}
        />
      )}
    </Box>
  );
};
